import { cloneDeep, get, has, isEqual, merge } from 'lodash';
import StandardIntegrationData from 'App/babel/resources/StandardIntegrationData';
import logError from 'App/babel/helpers/logError';
import getOrderChanges from 'App/helpers/getOrderChanges';
import { IntegrationInit } from 'App/resources/AllIWant';
import CustomField from 'App/resources/Model/CustomField';
import Order from 'App/resources/Model/Order';
import onOrderEditHelpers, { nonAllowedCustomDataTypes } from 'App/helpers/onOrderEditHelpers';

type ClientEditCustomValue = { id: number; value: unknown };
type ClientEditClient = { [field: string]: unknown; custom: ClientEditCustomValue[] };
export type ClientEditFieldTree = { [key: string]: boolean | ClientEditFieldTree };
type ClientEditUpdates = { fields: { [field: string]: unknown }; custom: ClientEditCustomValue[] };
export type ClientEditResult = { updates: ClientEditUpdates; visible: ClientEditFieldTree };

type ClientEditChanges = { [field: string]: unknown; custom?: { id: number }[] };

type ClientEditAppResponse = {
	updated?: { client?: { [field: string]: unknown; custom?: ClientEditCustomValue[] } };
	visible?: { client?: ClientEditFieldTree };
	listenTo?: { client?: ClientEditFieldTree };
};

type Listener = {
	integrationId: number;
	listenTo: string[] | null;
	visible: ClientEditFieldTree;
	lastUpdated?: string;
};

const CLIENT_EDIT_FIELDS = [
	'id',
	'name',
	'active',
	'webpage',
	'phone',
	'phoneCountryCode',
	'users',
	'categories',
	'projects',
	'parent',
	'journeyStep',
	'addresses',
	'notes',
	'fax',
	'status',
	'orgNo',
	'registrationDate',
	'sniCode',
	'companyForm',
	'creditRating',
	'turnover',
	'profit',
	'noEmployees',
	'facebook',
	'twitter',
	'linkedin',
	'sicCode',
	'naicsCode',
	'ukSicCode',
	'naceCode',
	'cfar',
	'about',
	'priceListId',
	'prospectingId',
	'prospectingCreditRating',
	'isMonitored'
];

const WRITABLE_FIELDS = [
	'name',
	'webpage',
	'phone',
	'phoneCountryCode',
	'journeyStep',
	'notes',
	'fax',
	'status',
	'orgNo',
	'registrationDate',
	'sniCode',
	'companyForm',
	'creditRating',
	'turnover',
	'profit',
	'noEmployees',
	'facebook',
	'twitter',
	'linkedin',
	'sicCode',
	'naicsCode',
	'ukSicCode',
	'naceCode',
	'cfar',
	'about',
	'priceListId'
];

const PROSPECTING_LOCKED_FIELDS = ['orgNo', 'cfar'];

const REFERENCE_LIST_FIELDS = ['users', 'categories', 'projects'];
const ADDRESS_FIELDS = ['address', 'zipcode', 'city', 'state', 'country'];

const emptyToNull = (value: unknown) => (value === '' || value === undefined ? null : value);

const toReference = (value: unknown) => {
	if (!value || typeof value !== 'object') {
		return null;
	}
	const { id, name } = value as { id?: unknown; name?: unknown };
	return { id: id ?? null, name: name ?? null };
};

const toAddresses = (value: unknown) => {
	const addresses = Array.isArray(value) ? (value as { [field: string]: unknown }[]) : [];
	return addresses
		.filter(address => ADDRESS_FIELDS.some(field => emptyToNull(address?.[field]) !== null))
		.map(address => ({
			type: address.type,
			...Object.fromEntries(ADDRESS_FIELDS.map(field => [field, emptyToNull(address[field])]))
		}));
};

const normalizeCustomValue = (value: unknown) => {
	if (Array.isArray(value)) {
		return value.filter(Boolean).join(',') || null;
	}
	return emptyToNull(value);
};

const normalizeField = (field: string, value: unknown) => {
	if (field === 'addresses') {
		return toAddresses(value);
	}
	if (REFERENCE_LIST_FIELDS.includes(field)) {
		return Array.isArray(value) ? value.map(toReference) : [];
	}
	if (field === 'parent') {
		return toReference(value);
	}
	return emptyToNull(value);
};

export const toClientEditClient = (
	source: { [field: string]: unknown },
	custom: ClientEditCustomValue[]
): ClientEditClient => {
	const client: ClientEditClient = {
		custom: custom.map(({ id, value }) => ({ id, value: normalizeCustomValue(value) })).sort((a, b) => a.id - b.id)
	};
	for (const field of CLIENT_EDIT_FIELDS) {
		client[field] = normalizeField(field, source[field]);
	}
	return client;
};

const lowerCaseKeys = (tree: ClientEditFieldTree): ClientEditFieldTree =>
	Object.fromEntries(
		Object.entries(tree).map(([key, value]) => [
			key.toLowerCase(),
			typeof value === 'object' ? lowerCaseKeys(value) : value
		])
	);

const listenedPaths = (tree: ClientEditFieldTree, prefix = ''): string[] =>
	Object.entries(tree).flatMap(([key, value]) => {
		const path = `${prefix}${key.toLowerCase()}`;
		if (typeof value === 'object') {
			return listenedPaths(value, `${path}.`);
		}
		return value === true ? [path] : [];
	});

const pathsOverlap = (a: string, b: string) => a === b || a.startsWith(`${b}.`) || b.startsWith(`${a}.`);

const changedPaths = (changes: ClientEditChanges) =>
	Object.keys(changes).flatMap(key =>
		key === 'custom' ? (changes.custom ?? []).map(({ id }) => `custom.${id}`) : [key.toLowerCase()]
	);

export const appFieldVisibility = (
	visible: ClientEditFieldTree,
	path: string,
	{ fallback, required = false }: { fallback?: boolean; required?: boolean } = {}
) => {
	const lowerCasePath = path.toLowerCase();
	const value = get(visible, lowerCasePath);
	const canForceVisible = lowerCasePath.startsWith('custom.');
	if (typeof value !== 'boolean' || (value && !canForceVisible) || (!value && required)) {
		return fallback;
	}
	return value;
};

const toCustomFieldValue = (field: CustomField, value: unknown) =>
	onOrderEditHelpers.getCustomFieldValue(
		field as Parameters<typeof onOrderEditHelpers.getCustomFieldValue>[0],
		(value ?? null) as Parameters<typeof onOrderEditHelpers.getCustomFieldValue>[1]
	);

export default class ClientEditListeners {
	private listeners: Listener[];
	private previous: ClientEditClient;
	private expected: ClientEditClient | null = null;
	private generation = 0;
	private disposed = false;

	constructor(
		integrations: IntegrationInit[],
		initial: ClientEditClient,
		private customFields: CustomField[],
		private onPending: (pending: boolean) => void,
		private onResult: (result: ClientEditResult) => void
	) {
		this.listeners = integrations.map(integration => ({
			integrationId: integration.id,
			listenTo: null,
			visible: {}
		}));
		this.previous = initial;
	}

	dispose() {
		this.disposed = true;
	}

	async onChange(client: ClientEditClient) {
		if (this.disposed) {
			return;
		}
		if (this.expected && isEqual(client, this.expected)) {
			this.previous = client;
			this.expected = null;
			return;
		}

		const changes = getOrderChanges(client as unknown as Order, this.previous as unknown as Order) as
			| ClientEditChanges
			| undefined;
		if (!changes) {
			return;
		}
		const paths = changedPaths(changes);
		const due = this.listeners.filter(
			listener =>
				!listener.listenTo || listener.listenTo.some(path => paths.some(changed => pathsOverlap(path, changed)))
		);
		if (!due.length) {
			return;
		}

		const previousClient = this.previous;
		const generation = ++this.generation;
		this.onPending(true);

		const responses = await Promise.all(due.map(listener => this.call(listener, client, previousClient, changes)));
		if (generation !== this.generation || this.disposed) {
			return;
		}
		this.previous = client;

		const fields: ClientEditUpdates['fields'] = {};
		const custom = new Map<number, unknown>();
		due.forEach((listener, index) => {
			const response = responses[index];
			if (response) {
				this.collect(listener, response, client, fields, custom);
			}
		});
		const updates: ClientEditUpdates = {
			fields,
			custom: [...custom].map(([id, value]) => ({ id, value }))
		};

		const hasUpdates = Object.keys(updates.fields).length > 0 || updates.custom.length > 0;
		this.expected = hasUpdates ? this.applyTo(client, updates) : null;
		this.onPending(false);
		this.onResult({
			updates,
			visible: merge({}, ...this.listeners.map(listener => listener.visible))
		});
	}

	private async call(
		listener: Listener,
		client: ClientEditClient,
		previousClient: ClientEditClient,
		changes: ClientEditChanges
	) {
		try {
			const response = await StandardIntegrationData.run<ClientEditAppResponse>(
				{
					type: 'clientedit',
					integrationId: listener.integrationId,
					data: { client, previousClient, changes }
				},
				{ params: { hideBodyLog: true } }
			);
			return response?.data ?? null;
		} catch (err) {
			logError(err, `clientedit failed for integration ${listener.integrationId}`);
			return null;
		}
	}

	private collect(
		listener: Listener,
		response: ClientEditAppResponse,
		client: ClientEditClient,
		fields: ClientEditUpdates['fields'],
		custom: Map<number, unknown>
	) {
		if (response.listenTo?.client) {
			listener.listenTo = listenedPaths(response.listenTo.client);
		}
		if (response.visible?.client) {
			listener.visible = lowerCaseKeys(response.visible.client);
		}

		const updated = JSON.stringify(response.updated ?? null);
		if (updated === listener.lastUpdated) {
			return;
		}
		listener.lastUpdated = updated;

		const { custom: customUpdates = [], ...fieldUpdates } = response.updated?.client ?? {};
		for (const [field, value] of Object.entries(fieldUpdates)) {
			const isScalar = value === null || typeof value !== 'object';
			const lockedByProspecting =
				PROSPECTING_LOCKED_FIELDS.includes(field) && !!client.prospectingId && client[field] !== null;
			if (WRITABLE_FIELDS.includes(field) && isScalar && !lockedByProspecting) {
				fields[field] = value;
			}
		}
		for (const customUpdate of customUpdates) {
			const field = this.customFields.find(customField => customField.id === customUpdate?.id);
			const writable = field?.$hasAccess && field.editable && !nonAllowedCustomDataTypes.includes(field.datatype);
			if (field && writable && has(customUpdate, 'value')) {
				custom.set(field.id, toCustomFieldValue(field, customUpdate.value));
			}
		}
	}

	private applyTo(client: ClientEditClient, updates: ClientEditUpdates): ClientEditClient {
		const next = cloneDeep(client);
		for (const [field, value] of Object.entries(updates.fields)) {
			next[field] = normalizeField(field, value);
		}
		next.custom = next.custom.map(entry => {
			const update = updates.custom.find(({ id }) => id === entry.id);
			return update ? { id: entry.id, value: normalizeCustomValue(update.value) } : entry;
		});
		return next;
	}
}
